import { useId, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal, useConfirmDialog } from '@safetyhub/ui';
import { adminApi } from '../lib/api';
import { ModalCancelButton } from '../components/ModalCancelButton';
import { RISK_MANAGEMENT_ACTIONS, getManagementAction, normalizedToLevel } from '@safetyhub/shared';

// ============================================
// 타입 정의
// ============================================

interface LikelihoodCriterion {
  level: number;
  label: string;
  description: string;
}

interface SeverityCriterion {
  level: number;
  label: string;
  description: string;
}

/**
 * 등급 경계표의 한 칸 (방식 무관 단일 정본 — 결정 2026-08-05)
 *
 * - 빈도·강도법: min/max는 빈도×강도 곱 구간
 * - 3단계 판단법: min/max는 정규화 점수(1~10) 구간
 *
 * ⚠️ **등급 번호는 저장하지 않는다** — min 오름차순 순서가 곧 등급 번호이고 라벨이 사람이 읽는
 * 이름을 이미 갖고 있다. 같은 정보를 두 곳에 두면 편집 화면에서 어긋날 통로가 생긴다
 * (2026-08-06 사용자 확정). 판정은 shared `scoreToRiskLevel`이 min 정렬 순번으로 파생한다.
 */
interface RiskLevelCriterion {
  min: number;
  max: number;
  label: string;
  managementAction: string;
  /** 판정 설명 (옛 levelCriteria 흡수분) */
  description?: string;
  color?: string;
}

/** 옛 3단계 기준 (경계 없음) — 읽기 폴백 전용, 신규 저장하지 않는다 */
interface LevelCriterion {
  level: number;
  label: string;
  description: string;
  managementAction?: string;
  color?: string;
}

/**
 * 3단계 세트의 등급 경계표를 구한다.
 *
 * 정본은 riskLevelCriteria. 아직 이전되지 않은 옛 세트는 levelCriteria에 **현재 판정에 쓰이는
 * 공식 경계**(normalizedToLevel 균등 분배)를 붙여 보여준다 — 화면에 없던 숫자를 지어내는 게 아니라
 * 이미 적용 중인 경계를 드러내는 것이다. 어떤 등급이 점수를 하나도 못 받으면(비정상) 변환하지 않는다.
 */
function toLevelBands(config: {
  riskLevelCriteria?: RiskLevelCriterion[];
  levelCriteria?: LevelCriterion[];
}): RiskLevelCriterion[] {
  const bands = config.riskLevelCriteria ?? [];
  if (bands.length > 0) return bands;

  const legacy = config.levelCriteria ?? [];
  if (legacy.length < 2) return [];

  const scores = new Map<number, number[]>();
  for (let score = 1; score <= 10; score++) {
    const matched = normalizedToLevel(score, legacy);
    if (!matched) return [];
    const bucket = scores.get(matched.level) ?? [];
    bucket.push(score);
    scores.set(matched.level, bucket);
  }
  if (scores.size !== legacy.length) return [];

  return [...legacy]
    .sort((a, b) => b.level - a.level)
    .map((l) => {
      const bucket = scores.get(l.level) ?? [];
      return {
        min: Math.min(...bucket),
        max: Math.max(...bucket),
        label: l.label,
        managementAction: l.managementAction || '',
        description: l.description,
        color: l.color,
      };
    });
}

/** 경계표를 표시 순서(위험 높은 순)로 정렬 — 순서 기준은 min 하나뿐이다(등급 번호는 저장하지 않는다) */
function sortBandsDesc(bands: RiskLevelCriterion[]): RiskLevelCriterion[] {
  return [...bands].sort((a, b) => b.min - a.min);
}

/** 옛 3단계 기준을 표시 순서(위험 높은 순)로 정렬 — 경계가 없어 등급 번호가 유일한 순서 기준이다. */
function sortLegacyDesc(levels: LevelCriterion[]): LevelCriterion[] {
  return [...levels].sort((a, b) => b.level - a.level);
}

/** 경계표 한 칸의 사람이 읽는 이름 — 에러 문구가 *어느 구간*을 고쳐야 하는지 짚을 수 있게. */
function bandName(c: RiskLevelCriterion): string {
  return `'${c.label.trim() || '이름 없음'}'(${c.min}~${c.max})`;
}

/** 숫자 뒤 조사 '로/으로' — 끝자리 읽기가 받침으로 끝나면(3·6·7·0) '으로'. */
function withRo(n: number): string {
  return [3, 6, 7, 0].includes(Math.abs(n) % 10) ? `${n}으로` : `${n}로`;
}

/**
 * 저장 직전 경계표 검증 — 정렬된 표에서 인접 구간이 **빈틈도 겹침도 없이** 이어지는지 본다.
 *
 * 판정(shared)은 이 두 결함을 조용히 흡수해버린다: 구간이 겹치면 `bands.find`가 **배열 순서
 * 첫 매치**를 고르고(화면은 min 순으로 정렬해 보여주므로 보는 순서와 판정 순서가 갈린다),
 * 빈틈에 떨어지면 매칭 실패로 옛 균등분배 공식으로 폴백한다. 둘 다 화면의 경계표와 실제 판정이
 * 어긋나는데 그 사실이 어디에도 드러나지 않는다 — 그래서 저장 자체를 막는다(2026-08-06 리뷰).
 *
 * 방식(빈도·강도 / 3단계) 무관 공용이다. 예전엔 3단계 분기에서만 길이·범위를 봐서 빈도·강도
 * 경계표를 통째로 지워도 저장됐고, 그러면 위험도 라벨이 빈칸으로 나왔다.
 *
 * @returns 문제가 있으면 저작자가 무엇을 고쳐야 하는지 짚는 문구, 없으면 null
 */
function validateBands(bands: RiskLevelCriterion[]): string | null {
  if (bands.length === 0) {
    return '위험성 수준 구간을 최소 1개 이상 입력해주세요. 구간이 하나도 없으면 위험도 라벨이 빈칸으로 표시됩니다.';
  }

  for (const c of bands) {
    if (!c.label.trim()) {
      return '이름이 비어 있는 구간이 있습니다. 모든 구간에 수준 이름(예: 상/중/하)을 입력해주세요.';
    }
    if (!Number.isFinite(c.min) || !Number.isFinite(c.max)) {
      return `${bandName(c)} 구간의 점수 범위를 숫자로 입력해주세요.`;
    }
    // 숫자칸을 비우면 0으로 들어온다 — 서버 @Min(1)에 걸려 "원인 없는 저장 실패"가 되므로 여기서 잡는다.
    if (c.min < 1) {
      return `${bandName(c)} 구간의 최소값은 1 이상이어야 합니다. 점수 범위 칸이 비어 있지 않은지 확인해주세요.`;
    }
    if (c.min > c.max) {
      return `${bandName(c)} 구간의 최소값이 최대값보다 큽니다.`;
    }
  }

  const asc = [...bands].sort((a, b) => a.min - b.min);
  for (let i = 1; i < asc.length; i++) {
    const prev = asc[i - 1];
    const cur = asc[i];
    if (cur.min <= prev.max) {
      const from = cur.min;
      const to = Math.min(prev.max, cur.max);
      const where = from === to ? `${from}점` : `${from}~${to}점`;
      return `${bandName(prev)} 구간과 ${bandName(cur)} 구간이 ${where}에서 겹칩니다. 뒤 구간을 ${prev.max + 1}부터 시작하거나 앞 구간의 최대값을 ${withRo(cur.min - 1)} 낮춰주세요.`;
    }
    if (cur.min > prev.max + 1) {
      const from = prev.max + 1;
      const to = cur.min - 1;
      const where = from === to ? `${from}점` : `${from}~${to}점`;
      return `${bandName(prev)} 구간과 ${bandName(cur)} 구간 사이 ${where}이 어느 구간에도 속하지 않습니다. 앞 구간의 최대값을 ${withRo(to)} 늘리거나 뒤 구간을 ${from}부터 시작해주세요.`;
    }
  }

  return null;
}

interface RiskAssessmentConfig {
  id: string;
  name: string;
  matrixType: string;
  methodType?: string;
  likelihoodCriteria: LikelihoodCriterion[];
  severityCriteria: SeverityCriterion[];
  riskLevelCriteria: RiskLevelCriterion[];
  levelCriteria?: LevelCriterion[];
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// 메인 컴포넌트
// ============================================

export default function RiskAssessment() {
  const queryClient = useQueryClient();
  const [confirm, confirmDialog] = useConfirmDialog();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState<RiskAssessmentConfig | null>(null);
  const [viewTarget, setViewTarget] = useState<RiskAssessmentConfig | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'risk-assessment-configs'],
    queryFn: () => adminApi.getRiskAssessmentConfigs(),
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => adminApi.setDefaultRiskAssessmentConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'risk-assessment-configs'] });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (id: string) => adminApi.toggleRiskAssessmentConfigActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'risk-assessment-configs'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteRiskAssessmentConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'risk-assessment-configs'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="bg-red-50 p-4 rounded text-red-600">
        데이터를 불러오는데 실패했습니다.
      </div>
    );
  }

  const configs = data.data || [];

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">위험성평가 설정</h2>
        <p className="mt-1 text-sm text-gray-500">
          KOSHA 기준 위험성평가 설정을 관리합니다.
        </p>
      </div>

      {/* 도구 바 */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          기본 설정은 새로운 점검 생성 시 자동으로 적용됩니다.
        </p>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <span>+</span>
          <span>설정 추가</span>
        </Button>
      </div>

      {/* 설정 목록 */}
      {configs.length === 0 ? (
        <div className="bg-gray-50 p-8 rounded text-center text-gray-500">
          등록된 위험성평가 설정이 없습니다.
        </div>
      ) : (
        <Card padding="none" radius="lg" className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left text-xs uppercase text-gray-500">
                <th className="px-4 py-3">이름</th>
                <th className="px-4 py-3">방식</th>
                <th className="px-4 py-3 text-center">빈도</th>
                <th className="px-4 py-3 text-center">강도</th>
                <th className="px-4 py-3">위험수준</th>
                <th className="px-4 py-3 text-center">상태</th>
                <th className="px-4 py-3 text-right">액션</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {[...configs]
                .sort((a, b) => {
                  if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
                  if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
                  return 0;
                })
                .map((config) => (
                  <ConfigRow
                    key={config.id}
                    config={config}
                    onView={() => setViewTarget(config)}
                    onEdit={() => setEditTarget(config)}
                    onSetDefault={() => {
                      if (!config.isDefault) {
                        setDefaultMutation.mutate(config.id);
                      }
                    }}
                    onToggleActive={() => toggleActiveMutation.mutate(config.id)}
                    onDelete={() => {
                      if (config.isDefault) {
                        alert('기본 설정은 삭제할 수 없습니다.');
                        return;
                      }
                      void confirm({
                        title: '설정 삭제',
                        body: '이 설정을 삭제하시겠습니까?',
                        confirmLabel: '삭제',
                        danger: true,
                      }).then((ok) => {
                        if (ok) deleteMutation.mutate(config.id);
                      });
                    }}
                  />
                ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* 추가/수정 모달 */}
      {(showAddModal || editTarget) && (
        <ConfigModal
          config={editTarget}
          onClose={() => {
            setShowAddModal(false);
            setEditTarget(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'risk-assessment-configs'] });
            setShowAddModal(false);
            setEditTarget(null);
          }}
        />
      )}

      {/* 상세 보기 모달 */}
      {viewTarget && (
        <ConfigViewModal config={viewTarget} onClose={() => setViewTarget(null)} />
      )}
      {confirmDialog}
    </div>
  );
}

// ============================================
// 설정 테이블 행
// ============================================

function ConfigRow({
  config,
  onView,
  onEdit,
  onSetDefault,
  onToggleActive,
  onDelete,
}: {
  config: RiskAssessmentConfig;
  onView: () => void;
  onEdit: () => void;
  onSetDefault: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}) {
  const isThreeLevel = config.methodType === 'THREE_LEVEL';

  return (
    <tr className={!config.isActive ? 'opacity-60 bg-gray-50' : 'hover:bg-gray-50'}>
      {/* 이름 */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-medium text-gray-900 truncate" title={config.name}>
            {config.name}
          </span>
          {config.isDefault && (
            <span className="shrink-0 px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded">기본</span>
          )}
        </div>
      </td>

      {/* 방식 */}
      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
        {isThreeLevel ? '3단계' : '빈도·강도'}
      </td>

      {/* 빈도 */}
      <td className="px-4 py-3 text-center text-gray-600">
        {isThreeLevel ? '-' : `${config.likelihoodCriteria.length}단계`}
      </td>

      {/* 강도 */}
      <td className="px-4 py-3 text-center text-gray-600">
        {isThreeLevel ? '-' : `${config.severityCriteria.length}단계`}
      </td>

      {/* 위험수준 미니 배지 */}
      <td className="px-4 py-3">
        <RiskLevelBadgesMini config={config} />
      </td>

      {/* 상태 */}
      <td className="px-4 py-3 text-center">
        <button
          onClick={onToggleActive}
          className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
            config.isActive
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          {config.isActive ? '활성' : '비활성'}
        </button>
      </td>

      {/* 액션 */}
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-3 whitespace-nowrap">
          <button onClick={onView} className="text-sm text-brand-600 hover:text-brand-800">
            상세
          </button>
          <button onClick={onEdit} className="text-sm text-gray-600 hover:text-gray-800">
            수정
          </button>
          {!config.isDefault && (
            <>
              <button onClick={onSetDefault} className="text-sm text-red-600 hover:text-red-800">
                기본으로
              </button>
              <button onClick={onDelete} className="text-sm text-red-400 hover:text-red-600">
                삭제
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

// ============================================
// 위험수준 미니 배지 (테이블 행용)
// ============================================

function RiskLevelBadgesMini({ config }: { config: RiskAssessmentConfig }) {
  const items: Array<{ label: string; colorClass: string }> = [];

  if (config.methodType === 'THREE_LEVEL') {
    const bands = sortBandsDesc(toLevelBands(config));
    // 경계표로 옮길 수 없는 옛 세트(비정상 levelCriteria)는 toLevelBands가 []를 준다 — 그대로 쓰면
    // 배지가 통째로 사라져 "척도가 없는 설정"처럼 보인다. 경계 없이 라벨만이라도 보여준다.
    const sorted: Array<{ label: string }> =
      bands.length > 0 ? bands : sortLegacyDesc(config.levelCriteria ?? []);
    sorted.forEach((l) => {
      const colorClass =
        l.label === '상'
          ? 'bg-red-100 text-red-700'
          : l.label === '중'
            ? 'bg-yellow-100 text-yellow-700'
            : 'bg-green-100 text-green-700';
      items.push({ label: l.label, colorClass });
    });
  } else {
    const sorted = [...config.riskLevelCriteria].sort((a, b) => b.min - a.min);
    sorted.forEach((r) => {
      const colorClass =
        r.label.includes('높') || r.label.includes('상')
          ? 'bg-red-100 text-red-700'
          : r.label.includes('보통') || r.label.includes('중')
            ? 'bg-yellow-100 text-yellow-700'
            : 'bg-green-100 text-green-700';
      items.push({ label: r.label, colorClass });
    });
  }

  if (items.length === 0) return <span className="text-xs text-gray-400">-</span>;

  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item, i) => (
        <span key={i} className={`px-1.5 py-0.5 rounded text-xs ${item.colorClass}`}>
          {item.label}
        </span>
      ))}
    </div>
  );
}

// ============================================
// 위험성 매트릭스 프리뷰
// ============================================

function RiskMatrixPreview({ config }: { config: RiskAssessmentConfig }) {
  // 복사본 정렬 — `config`는 쿼리 캐시가 들고 있는 객체라 렌더 중 배열을 직접 정렬하면 원본이 변형된다.
  const likelihood = [...config.likelihoodCriteria].sort((a, b) => b.level - a.level);
  const severity = [...config.severityCriteria].sort((a, b) => a.level - b.level);

  const getRiskLevel = (l: number, s: number) => {
    const score = l * s;
    const level = config.riskLevelCriteria.find(
      (r) => score >= r.min && score <= r.max
    );
    return level;
  };

  const getLevelColor = (label: string) => {
    if (label.includes('높') || label.includes('상')) return 'bg-red-500 text-white';
    if (label.includes('보통') || label.includes('중')) return 'bg-yellow-400 text-gray-900';
    return 'bg-green-400 text-white';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr>
            <th className="p-1"></th>
            {severity.map((s) => (
              <th key={s.level} className="p-1 text-center text-gray-500">
                {s.label}({s.level})
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {likelihood.map((l) => (
            <tr key={l.level}>
              <td className="p-1 text-gray-500 text-right whitespace-nowrap">
                {l.label}({l.level})
              </td>
              {severity.map((s) => {
                const level = getRiskLevel(l.level, s.level);
                return (
                  <td key={`${l.level}-${s.level}`} className="p-1">
                    <div
                      className={`w-8 h-8 flex items-center justify-center rounded text-xs font-medium ${
                        level ? getLevelColor(level.label) : 'bg-gray-200'
                      }`}
                    >
                      {l.level * s.level}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// 설정 상세 보기 모달
// ============================================

function ConfigViewModal({
  config,
  onClose,
}: {
  config: RiskAssessmentConfig;
  onClose: () => void;
}) {
  const bands = sortBandsDesc(toLevelBands(config));
  // 경계표로 옮길 수 없는 옛 세트는 toLevelBands가 []를 준다 — 그대로 그리면 표가 **행 0개**가 돼
  // 예전엔 보이던 저작자 척도가 사라진다. 이때는 levelCriteria를 그대로 보여주되 **점수 범위 칸은
  // 뺀다**(경계가 없는 데이터라 범위를 지어낼 수 없다).
  const hasBands = bands.length > 0;
  const rows: Array<{
    label: string;
    range: string | null;
    managementAction: string;
    description?: string;
  }> = hasBands
    ? bands.map((c) => ({
        label: c.label,
        range: c.min === c.max ? String(c.min) : `${c.min} ~ ${c.max}`,
        managementAction: c.managementAction,
        description: c.description,
      }))
    : sortLegacyDesc(config.levelCriteria ?? []).map((l) => ({
        label: l.label,
        range: null,
        managementAction: l.managementAction ?? '',
        description: l.description,
      }));

  return (
    // 종전 패널 스크롤(max-h-[90vh])은 base 본문 스크롤이 맡는다 — 헤더·닫기 줄이 고정된다.
    <Modal
      open
      onClose={onClose}
      title={config.name}
      size="xl"
      panelClassName="!max-w-3xl"
      footer={
        <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
          닫기
        </button>
      }
    >
        <p className="text-sm text-gray-500 mb-6">
          {config.methodType === 'THREE_LEVEL' ? '3단계 판단법' : `빈도·강도법 (${config.matrixType})`}
        </p>

        <div className="space-y-6">
          {/* ── 공통: 위험성 수준 관리기준 ── */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">위험성 수준 관리기준</h4>
            {!hasBands && rows.length > 0 && (
              <p className="mb-2 text-xs text-amber-700">
                점수 경계로 변환할 수 없는 옛 설정이라 수준 이름과 관리기준만 표시합니다(점수 범위 없음).
              </p>
            )}
            <div className="bg-gray-50 rounded overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">위험성 수준</th>
                    {hasBands && (
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">점수 범위</th>
                    )}
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">관리기준</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">내용</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {/* 방식 무관 단일 경계표 — 3단계도 riskLevelCriteria(옛 세트는 공식 경계)를 읽는다.
                      변환 자체가 불가능한 옛 세트는 levelCriteria로 폴백한다(rows 구성 참조). */}
                  {rows.map((c, i) => {
                    const ma = getManagementAction(c.managementAction);
                    return (
                      <tr key={i}>
                        <td className="px-4 py-2 text-sm">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            c.label.includes('높') || c.label.includes('상')
                              ? 'bg-red-100 text-red-700'
                              : c.label.includes('보통') || c.label.includes('중')
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {c.label}
                          </span>
                        </td>
                        {c.range !== null && (
                          <td className="px-4 py-2 text-sm font-medium">{c.range}</td>
                        )}
                        <td className="px-4 py-2 text-sm font-medium">{ma?.label || c.managementAction || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{ma?.description || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── 방식별 상세 ── */}
          {config.methodType === 'THREE_LEVEL' ? (
            /* 3단계: 수준별 판정 설명 */
            <div>
              <h4 className="font-medium text-gray-900 mb-3">수준별 판정 설명</h4>
              <div className="bg-gray-50 rounded overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">위험성 수준</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">판정 설명</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rows.map((l, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-sm">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            l.label === '상' ? 'bg-red-100 text-red-700'
                              : l.label === '중' ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {l.label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">{l.description || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* 빈도×강도: 매트릭스 + 빈도/강도 기준 */
            <>
              {/* 매트릭스 */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">위험성평가 매트릭스</h4>
                <div className="bg-gray-50 p-4 rounded">
                  <RiskMatrixPreview config={config} />
                </div>
              </div>

              {/* 빈도(가능성) 기준 */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">빈도(가능성) 기준</h4>
                <div className="bg-gray-50 rounded overflow-hidden">
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">등급</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">명칭</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">기준 설명</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {/* 복사본 정렬 — 쿼리 캐시가 들고 있는 배열을 렌더 중 변형하지 않는다. */}
                      {[...config.likelihoodCriteria]
                        .sort((a, b) => b.level - a.level)
                        .map((c) => (
                          <tr key={c.level}>
                            <td className="px-4 py-2 text-sm font-medium">{c.level}</td>
                            <td className="px-4 py-2 text-sm">{c.label}</td>
                            <td className="px-4 py-2 text-sm text-gray-600">{c.description}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 강도(중대성) 기준 */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">강도(중대성) 기준</h4>
                <div className="bg-gray-50 rounded overflow-hidden">
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">등급</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">명칭</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">기준 설명</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {/* 복사본 정렬 — 쿼리 캐시가 들고 있는 배열을 렌더 중 변형하지 않는다. */}
                      {[...config.severityCriteria]
                        .sort((a, b) => b.level - a.level)
                        .map((c) => (
                          <tr key={c.level}>
                            <td className="px-4 py-2 text-sm font-medium">{c.level}</td>
                            <td className="px-4 py-2 text-sm">{c.label}</td>
                            <td className="px-4 py-2 text-sm text-gray-600">{c.description}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* 메타 정보 */}
          <div className="text-sm text-gray-400 pt-4 border-t">
            <p>생성: {new Date(config.createdAt).toLocaleString('ko-KR')}</p>
            <p>수정: {new Date(config.updatedAt).toLocaleString('ko-KR')}</p>
          </div>
        </div>
    </Modal>
  );
}

// ============================================
// 설정 추가/수정 모달
// ============================================

// 색상 프리셋 (등급 경계표 공용 — 빈도·강도법 / 3단계 판단법 동일)
const COLOR_PRESETS = [
  { hex: '#dc2626', label: '빨강' },
  { hex: '#f97316', label: '주황' },
  { hex: '#eab308', label: '노랑' },
  { hex: '#84cc16', label: '연두' },
  { hex: '#22c55e', label: '초록' },
  { hex: '#3b82f6', label: '파랑' },
  { hex: '#8b5cf6', label: '보라' },
  { hex: '#6b7280', label: '회색' },
];

function ColorPicker({
  value,
  onChange,
}: {
  value?: string;
  onChange: (color: string) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      {COLOR_PRESETS.map((c) => (
        <button
          key={c.hex}
          type="button"
          title={c.label}
          onClick={() => onChange(c.hex)}
          className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${
            value === c.hex ? 'border-gray-800 scale-110' : 'border-transparent'
          }`}
          style={{ backgroundColor: c.hex }}
        />
      ))}
      <input
        type="color"
        value={value || '#6b7280'}
        onChange={(e) => onChange(e.target.value)}
        className="w-6 h-6 rounded cursor-pointer border border-gray-300"
        title="직접 선택"
      />
    </div>
  );
}

/**
 * 등급 경계표 편집기 — 빈도·강도법과 3단계 판단법이 같은 배열(riskLevelCriteria)을 쓰므로 한 컴포넌트다.
 * 3단계는 판정 설명(description) 칸이 추가로 보인다(withDescription — 등급 번호 칸을 걷어낸 뒤로
 * 이 플래그가 켜는 건 설명 칸 하나뿐이라 이름을 그 사실에 맞췄다).
 *
 * 표시는 위험 높은 순으로 정렬하되 수정·삭제는 **원본 인덱스**로 한다 — 정렬된 인덱스로 수정하면
 * 범위를 고쳐 순서가 바뀐 순간 엉뚱한 행이 수정된다.
 */
function RiskLevelCriteriaEditor({
  title,
  criteria,
  onChange,
  withDescription = false,
  hint,
}: {
  title: string;
  criteria: RiskLevelCriterion[];
  onChange: (next: RiskLevelCriterion[]) => void;
  /** 판정 설명(description) 입력칸 노출 — 3단계 판단법 전용. */
  withDescription?: boolean;
  hint?: string;
}) {
  const update = (index: number, field: keyof RiskLevelCriterion, value: string | number) => {
    onChange(criteria.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const rows = criteria
    .map((criterion, index) => ({ criterion, index }))
    .sort((a, b) => b.criterion.min - a.criterion.min);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">{title}</label>
        <button
          type="button"
          onClick={() =>
            onChange([
              ...criteria,
              withDescription
                ? {
                    min: 1,
                    max: 1,
                    label: '',
                    managementAction: 'MAINTAIN',
                    description: '',
                    color: '#6b7280',
                  }
                : { min: 1, max: 1, label: '', managementAction: 'IMMEDIATE', color: '#6b7280' },
            ])
          }
          className="text-sm text-red-600 hover:text-red-800"
        >
          + 추가
        </button>
      </div>
      {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}
      <div className="space-y-3">
        {rows.map(({ criterion: c, index }) => (
          <div key={index} className="border border-gray-200 rounded p-3 space-y-2">
            <div className="flex gap-2 items-center">
              <input
                type="number"
                value={c.min}
                onChange={(e) => update(index, 'min', parseInt(e.target.value) || 0)}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="최소"
                min={1}
              />
              <span className="text-gray-400">~</span>
              <input
                type="number"
                value={c.max}
                onChange={(e) => update(index, 'max', parseInt(e.target.value) || 0)}
                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="최대"
                min={1}
              />
              <input
                type="text"
                value={c.label}
                onChange={(e) => update(index, 'label', e.target.value)}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="수준"
              />
              <select
                value={c.managementAction}
                onChange={(e) => update(index, 'managementAction', e.target.value)}
                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
              >
                {RISK_MANAGEMENT_ACTIONS.map((ma) => (
                  <option key={ma.code} value={ma.code}>
                    {ma.label}: {ma.description}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => onChange(criteria.filter((_, i) => i !== index))}
                className="text-red-500 hover:text-red-700 shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {withDescription && (
              <input
                type="text"
                value={c.description || ''}
                onChange={(e) => update(index, 'description', e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="판정 설명"
              />
            )}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 shrink-0">색상:</span>
              <ColorPicker value={c.color} onChange={(color) => update(index, 'color', color)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ConfigModal({
  config,
  onClose,
  onSuccess,
}: {
  config: RiskAssessmentConfig | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const isEdit = !!config;

  const [name, setName] = useState(config?.name || '');
  const [matrixType, setMatrixType] = useState(config?.matrixType || '3x3');
  const [methodType, setMethodType] = useState<'FREQUENCY_SEVERITY' | 'THREE_LEVEL'>(
    (config?.methodType as 'FREQUENCY_SEVERITY' | 'THREE_LEVEL') || 'FREQUENCY_SEVERITY'
  );
  const [likelihoodCriteria, setLikelihoodCriteria] = useState<LikelihoodCriterion[]>(
    config?.likelihoodCriteria || [
      { level: 3, label: '상', description: '일반적 또는 반복적으로 발생' },
      { level: 2, label: '중', description: '발생 가능성 있음' },
      { level: 1, label: '하', description: '발생 가능성 거의 없음' },
    ]
  );
  const [severityCriteria, setSeverityCriteria] = useState<SeverityCriterion[]>(
    config?.severityCriteria || [
      { level: 3, label: '대', description: '사망·중대한 상해 또는 생명을 위협하는 직업성 질병 초래 위험' },
      { level: 2, label: '중', description: '의학적인 치료를 요하는 상해 또는 장애를 일으키는 질병' },
      { level: 1, label: '소', description: '아차사고, 무상해, 응급조치를 요하는 상해 또는 질병 초래 위험' },
    ]
  );
  // 빈도·강도법 경계표 (min/max = 빈도×강도 곱)
  const [riskLevelCriteria, setRiskLevelCriteria] = useState<RiskLevelCriterion[]>(
    (config?.methodType === 'THREE_LEVEL' ? undefined : config?.riskLevelCriteria) || [
      // 구간은 **빈틈 없이** 잇는다(3~5). 3x3 곱에 5는 나오지 않지만, 빈틈이 있으면 판정이
      // 조용히 옛 공식으로 폴백하고 저장 검증(validateBands)도 막는다 — 판정 결과는 종전과 동일.
      { min: 6, max: 9, label: '높음', managementAction: 'IMMEDIATE' },
      { min: 3, max: 5, label: '보통', managementAction: 'IMPROVE' },
      { min: 1, max: 2, label: '낮음', managementAction: 'MAINTAIN' },
    ]
  );
  // 3단계 경계표 (min/max = 정규화 점수 1~10) — 옛 세트는 현재 적용 중인 공식 경계로 채워 보여준다.
  // ⚠️ 등급 번호(level)는 넣지 않는다 — 서버 RiskLevelCriterionDto에 없는 필드라
  // ValidationPipe(whitelist + forbidNonWhitelisted)가 중첩 원소까지 거부해 400이 된다.
  const [threeLevelCriteria, setThreeLevelCriteria] = useState<RiskLevelCriterion[]>(() => {
    const existing = config?.methodType === 'THREE_LEVEL' ? toLevelBands(config) : [];
    if (existing.length > 0) return existing;
    return [
      { min: 8, max: 10, label: '상', description: '위험성이 높아 즉시 개선 필요', managementAction: 'IMMEDIATE', color: '#dc2626' },
      { min: 4, max: 7, label: '중', description: '위험성이 있어 개선 필요', managementAction: 'IMPROVE', color: '#eab308' },
      { min: 1, max: 3, label: '하', description: '위험성이 낮아 현 수준 유지', managementAction: 'MAINTAIN', color: '#22c55e' },
    ];
  });
  const [error, setError] = useState('');
  const formId = useId();

  // 미저장 닫기 보호 — 기준선은 첫 렌더의 편집 상태 전체(생성이면 기본 상/중/하 표). 기준표가 배열이라
  // 필드별 비교 대신 같은 식의 JSON 스냅샷을 대조한다(useState 초기값이라 첫 렌더 값으로 고정된다).
  const formSnapshot = JSON.stringify({
    name,
    matrixType,
    methodType,
    likelihoodCriteria,
    severityCriteria,
    riskLevelCriteria,
    threeLevelCriteria,
  });
  const [initialSnapshot] = useState(formSnapshot);
  const isDirty = formSnapshot !== initialSnapshot;

  // 옛 세트를 경계표로 옮기지 못한 경우(등급 중복 등 비정상 levelCriteria) 위 편집기는 **기본 상/중/하**로
  // 채워진다. 그걸 모른 채 저장하면 저작자가 만든 척도가 조용히 기본값으로 덮인다 — 그래서 경고한다.
  const scaleFallbackToDefaults =
    config?.methodType === 'THREE_LEVEL' &&
    toLevelBands(config).length === 0 &&
    (config.levelCriteria?.length ?? 0) > 0;

  const mutation = useMutation({
    mutationFn: () => {
      const isThreeLevel = methodType === 'THREE_LEVEL';
      // 경계표는 방식과 무관하게 riskLevelCriteria 하나에 저장한다(결정 2026-08-05).
      // levelCriteria는 신규 저장하지 않는다 — 옛 데이터 읽기 폴백으로만 남기므로 payload에서 뺀다.
      const data = {
        name,
        methodType,
        matrixType: isThreeLevel ? 'custom' : matrixType,
        likelihoodCriteria: isThreeLevel ? [] : likelihoodCriteria,
        severityCriteria: isThreeLevel ? [] : severityCriteria,
        // **정렬해서** 내보낸다 — 판정(shared `bands.find`)은 배열 순서 첫 매치를 고르는데 화면은
        // min 내림차순으로 보여주므로, 저장 순서가 삽입 순서면 보이는 표와 판정 순서가 갈릴 수 있다.
        // 표시 순서와 저장 순서를 같게 두면 그 통로 자체가 없어진다.
        riskLevelCriteria: sortBandsDesc(isThreeLevel ? threeLevelCriteria : riskLevelCriteria),
      };

      if (isEdit) {
        return adminApi.updateRiskAssessmentConfig(config.id, data);
      } else {
        return adminApi.createRiskAssessmentConfig(data);
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error?.message || '저장에 실패했습니다.');
      }
    },
    onError: () => {
      setError('저장 중 오류가 발생했습니다.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name) {
      setError('설정 이름을 입력해주세요.');
      return;
    }

    if (methodType !== 'THREE_LEVEL' && (likelihoodCriteria.length === 0 || severityCriteria.length === 0)) {
      setError('빈도와 강도 기준을 최소 1개 이상 입력해주세요.');
      return;
    }

    // 경계표 검증은 **방식과 무관**하게 같은 함수로 — 겹침·빈틈·0 이하 최소값·빈 표를 모두 여기서 막는다.
    const bandError = validateBands(methodType === 'THREE_LEVEL' ? threeLevelCriteria : riskLevelCriteria);
    if (bandError) {
      setError(bandError);
      return;
    }

    mutation.mutate();
  };

  const updateLikelihood = (index: number, field: keyof LikelihoodCriterion, value: string | number) => {
    setLikelihoodCriteria((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const updateSeverity = (index: number, field: keyof SeverityCriterion, value: string | number) => {
    setSeverityCriteria((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const isThreeLevel = methodType === 'THREE_LEVEL';

  return (
    // 종전 패널 스크롤(max-h-[90vh])은 base 본문 스크롤이 맡는다 — 헤더·저장 줄이 고정된다.
    // 제출 버튼이 base footer(폼 밖)에 있어 `form` 속성으로 묶는다.
    <Modal
      open
      onClose={onClose}
      title={isEdit ? '위험성평가 설정 수정' : '위험성평가 설정 추가'}
      size="xl"
      panelClassName="!max-w-3xl"
      isDirty={isDirty}
      footer={
        <>
          <ModalCancelButton onCancel={onClose} />
          <Button type="submit" form={formId} variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? '저장 중...' : '저장'}
          </Button>
        </>
      }
    >
        <form id={formId} onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* 옛 척도를 경계표로 옮기지 못해 기본값으로 채워진 경우 — 저장 전에 알린다(조용한 덮어쓰기 방지). */}
          {scaleFallbackToDefaults && isThreeLevel && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm">
              이 설정의 기존 수준 기준(
              {(config?.levelCriteria ?? []).map((l) => l.label).filter(Boolean).join(' / ') || '이름 없음'}
              )은 점수 경계로 변환할 수 없어(등급 중복 등) 아래 표가 <b>기본값(상/중/하)</b>으로 채워졌습니다.
              이대로 저장하면 기존 기준이 기본값으로 대체됩니다 — 필요한 값으로 고친 뒤 저장해주세요.
            </div>
          )}

          {/* 기본 정보 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                설정 이름 *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="예: 기본 3x3 매트릭스"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                평가 방식
              </label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="FREQUENCY_SEVERITY"
                    checked={methodType === 'FREQUENCY_SEVERITY'}
                    onChange={() => setMethodType('FREQUENCY_SEVERITY')}
                    className="text-red-600"
                  />
                  <span className="text-sm text-gray-700">빈도·강도법</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="THREE_LEVEL"
                    checked={methodType === 'THREE_LEVEL'}
                    onChange={() => setMethodType('THREE_LEVEL')}
                    className="text-red-600"
                  />
                  <span className="text-sm text-gray-700">3단계 판단법</span>
                </label>
              </div>
            </div>
          </div>

          {/* 매트릭스 타입 (빈도·강도법 전용) */}
          {!isThreeLevel && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                매트릭스 타입
              </label>
              <select
                value={matrixType}
                onChange={(e) => setMatrixType(e.target.value)}
                className="w-48 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="3x3">3x3</option>
                <option value="4x4">4x4</option>
                <option value="5x5">5x5</option>
                <option value="custom">커스텀</option>
              </select>
            </div>
          )}

          {/* ── 3단계 판단법 ── */}
          {isThreeLevel && (
            <RiskLevelCriteriaEditor
              title="위험성 수준 기준"
              criteria={threeLevelCriteria}
              onChange={setThreeLevelCriteria}
              withDescription
              hint="점수 범위는 정규화 위험도 점수(1~10) 기준입니다. 3등급 기본값은 1~3 하 / 4~7 중 / 8~10 상."
            />
          )}

          {/* ── 빈도·강도법 ── */}
          {!isThreeLevel && (
            <>
              {/* 빈도(가능성) 기준 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    빈도(가능성) 기준
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setLikelihoodCriteria((prev) => [
                        ...prev,
                        { level: prev.length + 1, label: '', description: '' },
                      ])
                    }
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    + 추가
                  </button>
                </div>
                <div className="space-y-2">
                  {/* 표시는 등급 내림차순, 수정·삭제는 **원본 인덱스**로 — 상태 배열을 렌더 중
                      직접 정렬하면(옛 코드) 상태가 변형되고, 등급을 고쳐 순서가 바뀌는 순간
                      엉뚱한 행이 수정된다(경계표 편집기와 같은 규약). */}
                  {likelihoodCriteria
                    .map((criterion, index) => ({ criterion, index }))
                    .sort((a, b) => b.criterion.level - a.criterion.level)
                    .map(({ criterion: c, index }) => (
                      <div key={index} className="flex gap-2 items-center">
                        <input
                          type="number"
                          value={c.level}
                          onChange={(e) => updateLikelihood(index, 'level', parseInt(e.target.value) || 0)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="등급"
                        />
                        <input
                          type="text"
                          value={c.label}
                          onChange={(e) => updateLikelihood(index, 'label', e.target.value)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="명칭"
                        />
                        <input
                          type="text"
                          value={c.description}
                          onChange={(e) => updateLikelihood(index, 'description', e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="기준 설명"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setLikelihoodCriteria((prev) => prev.filter((_, idx) => idx !== index))
                          }
                          className="text-red-500 hover:text-red-700"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                </div>
              </div>

              {/* 강도(중대성) 기준 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    강도(중대성) 기준
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setSeverityCriteria((prev) => [
                        ...prev,
                        { level: prev.length + 1, label: '', description: '' },
                      ])
                    }
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    + 추가
                  </button>
                </div>
                <div className="space-y-2">
                  {/* 빈도 목록과 같은 규약 — 복사본 정렬 + 원본 인덱스로 수정·삭제. */}
                  {severityCriteria
                    .map((criterion, index) => ({ criterion, index }))
                    .sort((a, b) => b.criterion.level - a.criterion.level)
                    .map(({ criterion: c, index }) => (
                      <div key={index} className="flex gap-2 items-center">
                        <input
                          type="number"
                          value={c.level}
                          onChange={(e) => updateSeverity(index, 'level', parseInt(e.target.value) || 0)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="등급"
                        />
                        <input
                          type="text"
                          value={c.label}
                          onChange={(e) => updateSeverity(index, 'label', e.target.value)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="명칭"
                        />
                        <input
                          type="text"
                          value={c.description}
                          onChange={(e) => updateSeverity(index, 'description', e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="기준 설명"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setSeverityCriteria((prev) => prev.filter((_, idx) => idx !== index))
                          }
                          className="text-red-500 hover:text-red-700"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                </div>
              </div>

              {/* 위험성 수준 판정 */}
              <RiskLevelCriteriaEditor
                title="위험성 수준 판정"
                criteria={riskLevelCriteria}
                onChange={setRiskLevelCriteria}
                hint="점수 범위는 빈도×강도 곱 기준입니다."
              />
            </>
          )}
        </form>
    </Modal>
  );
}
